import React, { useEffect, useMemo, useRef, useState } from "react";
import { Copy, QrCode, Smartphone } from "lucide-react";
import {
  AppButton, AppModal, Callout, FileUpload, FormField, IconButton, Input, NativeSelect, Textarea,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import { getQrConfig, submitQrPayment } from "../../utils/qrPaymentApi";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const IS_PHONE = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

/** `upi://pay` opens the phone's UPI app with the payee and amount filled in. */
function upiLink({ upiId, payeeName, amount, note }) {
  const p = new URLSearchParams({ pa: upiId, pn: payeeName || "", am: String(amount), cu: "INR", tn: note || "" });
  return `upi://pay?${p.toString()}`;
}

/**
 * Pay the institute's UPI QR code and upload the proof.
 *
 * The same dialog for all three things a student pays for — joining a batch
 * course, buying a self-paced course, and paying fees they already owe. For an
 * enrolment the amount is the course price and is not editable (the server sets
 * it regardless); for fees the student says how much they paid, up to what they
 * owe.
 *
 * It promises nothing it cannot keep: the student is told their payment will be
 * checked, and that access (or the receipt) follows approval — never that they
 * are enrolled now.
 *
 * `choices` (fees only): every course the student owes on, as
 * `{ key, courseAssignmentId, courseName, max, defaultAmount }`. With more than
 * one, the student says which the payment was for — used when they arrive from
 * the QR itself rather than from one course's Pay button.
 */
export default function QrPayModal({
  show, onHide, purpose, courseId, batchId, courseAssignmentId: givenCaId, courseName: givenCourseName,
  amount: fixedAmount, maxAmount: givenMax, choices, config: givenConfig, onSubmitted,
}) {
  const isFee = purpose === "fee_payment";
  const [choiceKey, setChoiceKey] = useState(null);
  const choice = choices?.length ? (choices.find((c) => c.key === choiceKey) || choices[0]) : null;
  const courseAssignmentId = choice ? choice.courseAssignmentId : givenCaId;
  const courseName = choice ? choice.courseName : givenCourseName;
  const maxAmount = choice ? choice.max : givenMax;
  const [config, setConfig] = useState(givenConfig || null);
  const [configError, setConfigError] = useState("");
  const [amount, setAmount] = useState("");
  const [files, setFiles] = useState([]);
  const [utr, setUtr] = useState("");
  const [paidOn, setPaidOn] = useState(today());
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Reset only when the dialog opens or the set of courses changes — never on a
  // parent re-render that hands over an equal-but-new `choices` array, which
  // would wipe the screenshot and UTR mid-typing.
  const choicesRef = useRef(choices);
  choicesRef.current = choices;
  const choicesSig = (choices || []).map((c) => c.key).join("|");

  useEffect(() => {
    if (!show) return;
    const list = choicesRef.current;
    setFiles([]);
    setUtr("");
    setPaidOn(today());
    setNote("");
    setError("");
    setCopied(false);
    setChoiceKey(list?.[0]?.key ?? null);
    const first = list?.[0]?.defaultAmount ?? fixedAmount;
    setAmount(first != null ? String(first) : "");
    if (givenConfig) { setConfig(givenConfig); return; }
    let cancelled = false;
    setConfig(null);
    setConfigError("");
    getQrConfig()
      .then(({ data }) => { if (!cancelled) setConfig(data?.data || { enabled: false }); })
      .catch(() => { if (!cancelled) setConfigError("Couldn't load the payment details. Please try again."); });
    return () => { cancelled = true; };
  }, [show, givenConfig, fixedAmount, choicesSig]);

  const pickChoice = (key) => {
    setChoiceKey(key);
    const next = choices.find((c) => c.key === key);
    setAmount(next?.defaultAmount != null ? String(next.defaultAmount) : "");
  };

  const preview = useMemo(() => {
    const f = files[0];
    if (!f || !f.type.startsWith("image/")) return null;
    return URL.createObjectURL(f);
  }, [files]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const numericAmount = Number(String(amount).replace(/[,\s₹]/g, ""));
  const amountError = isFee && amount !== "" && (!(numericAmount > 0)
    ? "Enter the amount you paid."
    : maxAmount != null && numericAmount > maxAmount ? `You owe ${money(maxAmount)} — pay at most that.` : "");
  const utrClean = utr.toUpperCase().replace(/[\s-]/g, "");
  const utrError = utr && !/^[A-Z0-9]{6,35}$/.test(utrClean) ? "Use the letters and numbers only — a UPI ID is usually 12 digits." : "";
  const canSubmit = !!files[0] && !!utrClean && !utrError && numericAmount > 0 && !amountError && !submitting;

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(config.upiId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the ID is on screen to copy by hand */ }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await submitQrPayment({
        purpose, courseId, batchId, courseAssignmentId,
        amount: isFee ? numericAmount : undefined,
        utr: utrClean, paidOn, note: note.trim(),
        proof: files[0],
      });
      onSubmitted?.(data?.data, data?.message);
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't send your payment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const ready = config && config.enabled;
  const payAmount = numericAmount > 0 ? numericAmount : null;

  const footer = ready ? (
    <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
      <AppButton variant="outline" onClick={onHide} disabled={submitting}>Cancel</AppButton>
      <AppButton onClick={submit} disabled={!canSubmit}>
        {submitting ? "Sending…" : "Send for approval"}
      </AppButton>
    </div>
  ) : null;

  return (
    <AppModal
      show={show}
      onHide={submitting ? undefined : onHide}
      size="lg"
      icon={<QrCode size={18} />}
      title={isFee ? "Pay by UPI QR" : `Pay for ${courseName || "this course"}`}
      footer={footer}
      closeOnOverlay={!submitting}
    >
      {!config && !configError && <Shimmer height={260} />}
      {configError && <Callout tone="danger" role="alert">{configError}</Callout>}
      {config && !config.enabled && (
        <Callout tone="info">UPI QR payments aren't available right now. Please contact your institute.</Callout>
      )}

      {ready && (
        <div className="tw-grid tw-gap-5 md:tw-grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
          {/* ── 1. Pay ── */}
          <section aria-labelledby="qrpay-step1" className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-center">
            <h3 id="qrpay-step1" className="tw-m-0 tw-text-[13px] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-content-muted">
              1 · Scan and pay
            </h3>
            <div className="tw-flex tw-flex-col tw-gap-0.5">
              <span className="tw-text-2xl tw-font-bold tw-text-content">{payAmount ? money(payAmount) : "—"}</span>
              {config.payeeName && <span className="tw-text-[13px] tw-text-content-muted">to {config.payeeName}</span>}
            </div>
            {config.qrImageUrl ? (
              <img
                src={config.qrImageUrl}
                alt={`UPI QR code${config.payeeName ? ` for ${config.payeeName}` : ""}`}
                className="tw-w-full tw-max-w-[15rem] tw-rounded-md tw-border tw-border-solid tw-border-line"
              />
            ) : (
              <Callout tone="info">Pay to the UPI ID below from any UPI app.</Callout>
            )}
            {config.upiId && (
              <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[13px]">
                <span className="tw-text-content-muted">UPI ID</span>
                <code className="tw-rounded-sm tw-bg-muted tw-px-1.5 tw-py-0.5 tw-text-[13px] tw-text-content">{config.upiId}</code>
                <IconButton label={copied ? "Copied" : "Copy UPI ID"} icon={Copy} size="xs" variant="ghost" onClick={copyUpi} />
              </div>
            )}
            {IS_PHONE && config.upiId && payAmount && (
              <AppButton
                as="a"
                variant="outline"
                size="sm"
                href={upiLink({ upiId: config.upiId, payeeName: config.payeeName, amount: payAmount, note: courseName })}
              >
                <Smartphone size={15} /> Open my UPI app
              </AppButton>
            )}
            {config.instructions && (
              <p className="tw-m-0 tw-whitespace-pre-line tw-text-left tw-text-[13px] tw-text-content-muted">{config.instructions}</p>
            )}
          </section>

          {/* ── 2. Prove ── */}
          <section aria-labelledby="qrpay-step2" className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3">
            <h3 id="qrpay-step2" className="tw-m-0 tw-text-[13px] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-content-muted">
              2 · Upload the proof
            </h3>

            {isFee && choices?.length > 1 && (
              <FormField label="This payment is for" required>
                <NativeSelect value={choice?.key || ""} onChange={(e) => pickChoice(e.target.value)}>
                  {choices.map((c) => (
                    <option key={c.key} value={c.key}>{c.courseName} — {money(c.max)} due</option>
                  ))}
                </NativeSelect>
              </FormField>
            )}

            {isFee && (
              <FormField
                label="Amount you paid"
                required
                error={amountError || undefined}
                hint={maxAmount != null ? `You owe ${money(maxAmount)}${courseName ? ` for ${courseName}` : ""}.` : undefined}
              >
                <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
              </FormField>
            )}

            <FileUpload
              files={files}
              onChange={setFiles}
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              multiple={false}
              maxFiles={1}
              maxSizeMB={8}
              label="Payment screenshot"
              hint="Drop the screenshot of the successful payment here, or click to choose"
            />
            {preview && (
              <img src={preview} alt="Your screenshot" className="tw-max-h-48 tw-self-start tw-rounded-md tw-border tw-border-solid tw-border-line" />
            )}

            <FormField
              label="UPI transaction ID (UTR)"
              required
              error={utrError || undefined}
              hint="In your UPI app, open this payment's details — it's the 12-digit “UPI transaction ID” or “UTR”."
            >
              <Input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 412356789012" autoComplete="off" />
            </FormField>

            <FormField label="Paid on">
              <Input type="date" value={paidOn} max={today()} onChange={(e) => setPaidOn(e.target.value)} />
            </FormField>

            <FormField label="Note for the institute" hint="Optional — e.g. paid from a parent's account.">
              <Textarea rows={2} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
            </FormField>

            <Callout tone="info">
              {isFee
                ? "Your institute checks this payment against their bank. Once it's approved, your balance updates and the invoice appears under Payment history."
                : "Your institute checks this payment against their bank. You get access to the course once it's approved, and we'll notify you either way."}
            </Callout>
            {error && <Callout tone="danger" role="alert">{error}</Callout>}
          </section>
        </div>
      )}
    </AppModal>
  );
}
